import { useTranslation } from "next-i18next";
import Link from "next/link";
import Image from "next/image";
import { formatUSD } from "@/utils";
import { Button, Pagination, Table, PaginationProps } from "antd";
import { useEffect, useState } from "react";
import { getIBDonationList } from "@/api/donation";
import { IUserStats } from "@/props/IDonation";
import { ColumnsType } from "antd/es/table";
import MobileClientRow from "./MobileClientRow";
import Icon from "../Icon";
import DonationInfo from "./DonationInfo";

// 我的累积捐赠
const RaceStates = ({
  isIB,
  isMobile,
  isLogin,
  userStats,
}: {
  isIB: boolean;
  isMobile: boolean;
  isLogin: boolean;
  userStats: IUserStats;
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [searchInput, setSearchInput] = useState<string>("");
  const [tableData, setTableData] = useState<IUserStats[]>([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 5,
  });

  const labelTextSize = isMobile ? "text-base" : "text-2xl";

  useEffect(() => {
    if (isIB) {
      getClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  // 处理输入框变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
  };

  const tableColumns: ColumnsType<IUserStats> = [
    {
      dataIndex: "index",
      key: "index",
      render: (_, record, index) => {
        return isMobile ? (
          <MobileClientRow record={record} t={t} />
        ) : (
          <div key={index} className="flex py-6">
            <div className="w-1/4 pr-4 text-2xl font-semibold">
              <div className="mb-1">{record.name}</div>
              <div className="text-[#9ca3af]">
                {t("csr.uuid")}
                {record.uuid}
              </div>
            </div>
            <div className="flex w-3/4 justify-between">
              <div className="text-left">
                <div className="mb-3 text-base text-[#9ca3af]">{t("csr.client_investment_donation")}</div>
                <div className="text-2xl font-semibold">{formatUSD(record?.totalTradingDonationAmountUsd)} USD</div>
              </div>
              <div className="text-left">
                <div className="mb-3 text-base text-[#9ca3af]">{t("csr.client_dream_donation")}</div>
                <div className="text-2xl font-semibold">
                  {formatUSD(record?.totalDreamPointsDonationAmountUsd)} USD{" "}
                  <span className="text-xs font-semibold">
                    （{record.totalDreamPointsDonationAmountUsd * 1000}
                    {t("common.dream_value")}）
                  </span>
                </div>
              </div>
              <div className="text-left">
                <div className="mb-3 text-base text-[#9ca3af]">{t("csr.donation_total")}</div>
                <div className="text-2xl font-semibold">{formatUSD(record?.totalDonationAmountUsd)} USD</div>
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  const onPageChange: PaginationProps["onChange"] = (page, pageSize) => {
    const data = JSON.parse(JSON.stringify(queryParams));
    data.page = page;
    data.limit = pageSize;
    setQueryParams(data);
  };

  const getClients = async () => {
    setLoading(true);
    try {
      const data: any = await getIBDonationList({ ...queryParams, searchValue: searchInput });
      setTableData(data.list);
      setTotal(data.total);
      setLoading(false);
    } catch (errorInfo) {
      console.log("Failed:", errorInfo);
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Background images using Next.js Image */}
      <div className="absolute inset-0 -z-10 h-full w-full">
        <Image
          src={isMobile ? "/images/csr/race_mobile_bg1.webp" : "/images/csr/race_bg_1.webp"}
          alt="background"
          fill
          className="object-cover"
          quality={100}
        />
      </div>
      <div className={`m-auto max-w-[1440px] ${isMobile ? "px-4 py-[40px]" : "py-[120px]"}`}>
        <h3
          className={`text-center font-semibold text-stone-800 ${
            isMobile ? "mb-6 h-[48px] text-2xl" : "mb-[68px] h-[60px] text-5xl"
          }`}
        >
          {t("csr.my_donation")}
        </h3>
        <div className={`flex ${isMobile ? "mb-2 text-base" : "mb-4 text-2xl"}`}>
          <p className="flex-1 font-semibold text-stone-800">
            {t("csr.uuid")} {userStats?.uuid}
          </p>
          <Link href="/user/points?tab=donation" className="flex items-center">
            <span className="font-semibold text-primary">{t("csr.check_donation")}</span>
            <Icon name="arrow-right" isMobile size={16} className="mr-4" />
          </Link>
        </div>
        {/* 捐赠信息区块 */}
        <DonationInfo isIB={isIB} isMobile={isMobile} userStats={userStats} />

        {isIB && (
          <div>
            <p
              className={`font-semibold text-stone-800 ${labelTextSize} ${
                isMobile ? "mb-4 mt-[40px] text-center" : "mb-6 mt-[64px]"
              }`}
            >
              {t("csr.client_detail")}
            </p>
            <div className="rounded-xl">
              <div className={`flex items-center gap-2 ${isMobile ? "mb-4" : "mb-8"}`}>
                <div className="relative">
                  <input
                    value={searchInput}
                    onChange={handleInputChange}
                    type="text"
                    placeholder={t("csr.name_or_uuid")}
                    className={`rounded-full border border-gray-300 bg-white px-4 text-base focus:outline-none ${
                      isMobile ? "w-[226px] py-3" : "w-72 py-4"
                    }`}
                  />
                  <Icon name="search" size={24} title="icon search" className="absolute right-4 top-4" />
                </div>
                <Button
                  className="ml-2 rounded-full border-none bg-white px-10 py-6 text-base text-primary"
                  onClick={getClients}
                >
                  {t("csr.search")}
                </Button>
              </div>

              <div
                className={`custom-donation-table divide-y divide-stone-400 border-stone-400 text-sm
                  ${isMobile ? "border-t" : "border-b"} 
                  ${tableData.length <= 0 ? "border-none" : ""}`}
              >
                <Table
                  showHeader={false}
                  dataSource={tableData}
                  columns={tableColumns}
                  pagination={false}
                  loading={loading}
                  rowKey="uuid"
                />
              </div>

              <div className={`mt-4 flex items-center justify-between ${isMobile ? "flex-col" : ""}`}>
                <span className={`text-sm text-[#4b5563] ${isMobile ? "mb-2" : ""}`}>
                  {t("csr.total")} {total} {t("csr.total_data")}
                </span>
                <Pagination
                  current={queryParams.page}
                  total={total}
                  pageSize={5}
                  onChange={onPageChange}
                  showSizeChanger={false}
                  showQuickJumper={true}
                  className="
                    [&_.ant-pagination-disabled_.ant-pagination-item-link]:!text-[#6b7280]
                    [&_.ant-pagination-item-active]:!border-none
                    [&_.ant-pagination-item-active]:!bg-primary
                    [&_.ant-pagination-item-active]:!text-white
                    [&_.ant-pagination-item-active_a]:!text-white
                    [&_.ant-pagination-item:hover]:!bg-primary
                    [&_.ant-pagination-item:hover_a]:!text-white
                    [&_.ant-pagination-item]:!border-none
                    [&_.ant-pagination-item]:bg-[#feeaeb]
                    [&_.ant-pagination-item_a]:text-[#4b5563]
                    [&_.ant-pagination-next_.ant-pagination-item-link]:!text-primary
                    [&_.ant-pagination-options-quick-jumper]:!ml-2
                    [&_.ant-pagination-options-quick-jumper_input]:!w-[72px]
                    [&_.ant-pagination-options-quick-jumper_input]:!bg-transparent
                    [&_.ant-pagination-options-quick-jumper_input]:!px-0
                    [&_.ant-pagination-options-quick-jumper_input]:!text-center
                    [&_.ant-pagination-options-quick-jumper_input]:caret-primary
                    [&_.ant-pagination-options-quick-jumper_input]:focus:!border-b-primary
                    [&_.ant-pagination-prev_.ant-pagination-item-link]:!text-primary
                  "
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx global>
        {`
          .custom-donation-table .ant-table,
          .custom-donation-table .ant-table-container,
          .custom-donation-table .ant-table-content,
          .custom-donation-table table,
          .custom-donation-table thead > tr > th,
          .custom-donation-table tbody > tr > td {
            background-color: transparent !important;
          }
        `}
      </style>
    </div>
  );
};

export default RaceStates;
